import React, { createContext, useContext, useEffect, useState } from 'react';
import type { User } from 'firebase/auth';
import { loadProfile, watchAuth, watchProfile } from '../firebase/authService';
import { isConfigured } from '../firebase/config';
import { isMasterEmail, type AppUser } from '../types';

interface AuthState {
  user: AppUser | null;
  /** Signed in with Firebase Auth — true even before the profile document arrives. */
  authed: boolean;
  /** The Firebase Auth uid — set as soon as the credential exists, profile or no profile. */
  uid: string | null;
  email: string | null;
  /** Auth state not known yet. */
  loading: boolean;
  /** Signed in, and the users/{uid} document has reported at least once. */
  profileChecked: boolean;
  configured: boolean;
  refresh: () => Promise<void>;
}
const Ctx = createContext<AuthState>({
  user: null, authed: false, uid: null, email: null, loading: true,
  profileChecked: false, configured: false, refresh: async () => {},
});
export const useAuth = () => useContext(Ctx);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [fbUser, setFbUser] = useState<User | null>(null);
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileChecked, setProfileChecked] = useState(false);

  useEffect(() => {
    if (!isConfigured) { setLoading(false); return; }
    return watchAuth(raw => {
      // An anonymous sign-in is the tech link's, not a person's: it has no user
      // record and no business in the main app. Treat it as signed out here —
      // signing in as somebody real replaces it.
      const u = raw && !raw.isAnonymous ? raw : null;
      setFbUser(u);
      setLoading(false);
      if (!u) { setUser(null); setProfileChecked(false); }
    });
  }, []);

  // Watch the profile rather than reading it once: signUp() writes users/{uid}
  // a moment after the credential exists, and an admin may change a role in the
  // console while the app is open. Both now land without a reload.
  // The master admin is the master by email, not by record: with no users
  // document at all, the sign-in still opens on the master dashboard. A record,
  // where one exists, supplies the name and phone and nothing more.
  useEffect(() => {
    const uid = fbUser?.uid;
    if (!uid) return;
    const email = fbUser?.email ?? '';
    setProfileChecked(false);
    return watchProfile(uid, p => {
      setUser(p ?? (isMasterEmail(email)
        ? { uid, email, name: '', role: 'admin', company: 'BlueTape Pro', projects: [],
            accountId: '__master', createdAt: '' }
        : null));
      setProfileChecked(true);
    });
  }, [fbUser?.uid]);

  const refresh = async () => { if (fbUser) setUser(await loadProfile(fbUser.uid)); };

  return (
    <Ctx.Provider value={{
      user, authed: !!fbUser, uid: fbUser?.uid ?? null, email: fbUser?.email ?? null,
      loading, profileChecked, configured: isConfigured, refresh,
    }}>
      {children}
    </Ctx.Provider>
  );
}
